import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import LicenseExpired from "../pages/LicenseExpired";

const LicenseContext = createContext();

export const useLicense = () => useContext(LicenseContext);

// Cache keys for sessionStorage
const LICENSE_CACHE_KEY = "license_status";
const LICENSE_CACHE_TIME_KEY = "license_check_time";
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export default function LicenseProvider({ children }) {
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isValid, setIsValid] = useState(false);

  // Check if cached license is still valid
  const getCachedLicense = () => {
    try {
      const cachedStatus = sessionStorage.getItem(LICENSE_CACHE_KEY);
      const cachedTime = sessionStorage.getItem(LICENSE_CACHE_TIME_KEY);
      
      if (cachedStatus && cachedTime) {
        const timeDiff = Date.now() - parseInt(cachedTime);
        if (timeDiff < CACHE_DURATION) {
          // Cache is still valid
          return JSON.parse(cachedStatus);
        }
      }
    } catch (e) {
      console.error("Error reading license cache:", e);
    }
    return null;
  };

  // Save license to cache
  const cacheLicense = (status, valid) => {
    try {
      sessionStorage.setItem(LICENSE_CACHE_KEY, JSON.stringify({ status, isValid: valid }));
      sessionStorage.setItem(LICENSE_CACHE_TIME_KEY, Date.now().toString());
    } catch (e) {
      console.error("Error caching license:", e);
    }
  };

  const checkLicense = async (showLoading = false) => {
    try {
      if (showLoading) setLoading(true);
      
      const response = await axios.get("/api/License/validate");
      
      setLicenseStatus(response.data.status);
      setIsValid(response.data.isValid);
      
      // Cache the result
      cacheLicense(response.data.status, response.data.isValid);
      
      return response.data.isValid;
    } catch (err) {
      console.error("License check failed:", err);
      
      // If server is down or other error, check if it's a license error
      if (err.response?.data?.error === "LICENSE_INVALID") {
        setLicenseStatus(err.response.data);
        setIsValid(false);
        cacheLicense(err.response.data, false);
      } else {
        // For other errors (like network), allow app to work
        setIsValid(true);
        cacheLicense(null, true);
      }
      
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // First, check if we have a valid cached license
    const cached = getCachedLicense();
    
    if (cached) {
      // Use cached data immediately (no loading screen)
      setLicenseStatus(cached.status);
      setIsValid(cached.isValid);
      setLoading(false);
      
      // Still check in background to refresh cache
      checkLicense(false);
    } else {
      // No cache, need to check license (show loading briefly)
      checkLicense(true);
    }
    
    // Re-check license every 5 minutes (silently in background)
    const interval = setInterval(() => checkLicense(false), CACHE_DURATION);
    
    return () => clearInterval(interval);
  }, []);

  const handleLicenseActivated = () => {
    // Clear cache and recheck
    sessionStorage.removeItem(LICENSE_CACHE_KEY);
    sessionStorage.removeItem(LICENSE_CACHE_TIME_KEY);
    checkLicense(true);
  };

  // Show loading spinner only on first visit (when no cache exists)
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingContent}>
          <div style={styles.spinner}></div>
          <p style={styles.loadingText}>Validating License...</p>
        </div>
      </div>
    );
  }

  // Show license expired/invalid page if license is not valid
  if (!isValid) {
    return (
      <LicenseExpired 
        licenseStatus={licenseStatus} 
        onLicenseActivated={handleLicenseActivated} 
      />
    );
  }

  // License is valid, render children
  return (
    <LicenseContext.Provider value={{ licenseStatus, isValid, checkLicense }}>
      {/* License warning banner if expiring soon */}
      {licenseStatus?.daysRemaining <= 30 && licenseStatus?.daysRemaining > 0 && (
        <div style={styles.warningBanner}>
          ⚠️ Your license expires in {licenseStatus.daysRemaining} days. Please renew soon.
        </div>
      )}
      {children}
    </LicenseContext.Provider>
  );
}

const styles = {
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f0f2f5"
  },
  loadingContent: {
    textAlign: "center"
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #1a73e8",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto 20px"
  },
  loadingText: {
    color: "#666",
    fontSize: "16px"
  },
  warningBanner: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "10px 20px",
    textAlign: "center",
    fontSize: "14px",
    fontWeight: "500",
    position: "sticky",
    top: 0,
    zIndex: 9999
  }
};

// Add CSS animation for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
